import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar as CalendarIcon, Loader2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { paymentService } from "@/lib/api/services/payment.service";
import { authService } from "@/lib/api/services/auth.service";
import type { Transaction } from "@/lib/api/types";
import { useToast } from "@/hooks/use-toast";

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return format(date, "dd MMM yyyy, hh:mm a");
  } catch {
    return dateString;
  }
};

const formatDateForAPI = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

const TransactionsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = authService.getCurrentUser();
  const firmId = user?.firmId ? Number(user.firmId) : 1;

  const [fromDate, setFromDate] = useState<Date | undefined>(new Date());
  const [toDate, setToDate] = useState<Date | undefined>(new Date());
  const [status, setStatus] = useState<string>("ALL");
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  const handleView = async () => {
    if (!fromDate || !toDate) {
      toast({
        title: "Validation Error",
        description: "Please select both From Date and To Date",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await paymentService.getTransactionsList({
        firmId,
        fromDate: formatDateForAPI(fromDate),
        toDate: formatDateForAPI(toDate),
        status,
      });
      setTransactions(response || []);
      toast({
        title: "Success",
        description: `Found ${response?.length || 0} transactions`,
      });
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast({
        title: "Error",
        description: "Failed to fetch transactions. Please try again.",
        variant: "destructive",
      });
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (paymentStatus: string) => {
    const statusLower = paymentStatus.toLowerCase();
    if (statusLower === "success") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          SUCCESS
        </span>
      );
    } else if (statusLower === "failed" || statusLower === "user_dropped") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
          {paymentStatus.toUpperCase()}
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
          {paymentStatus.toUpperCase()}
        </span>
      );
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/my-account")}
            className="mb-4 sm:mb-6 text-sm sm:text-base text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Account
          </Button>

          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Transactions List</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                View and filter your payment transactions
              </p>
            </div>

            {/* Filters Card */}
            <Card className="mb-6 shadow-sm border-border/60">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl text-foreground">Filter Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* From Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">From Date</label>
                    <Popover open={showFromCalendar} onOpenChange={setShowFromCalendar}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !fromDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {fromDate ? format(fromDate, "dd/MM/yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={fromDate}
                          onSelect={(date) => {
                            setFromDate(date);
                            setShowFromCalendar(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* To Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">To Date</label>
                    <Popover open={showToCalendar} onOpenChange={setShowToCalendar}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !toDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {toDate ? format(toDate, "dd/MM/yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={toDate}
                          onSelect={(date) => {
                            setToDate(date);
                            setShowToCalendar(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Status */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Status</label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">ALL</SelectItem>
                        <SelectItem value="SUCCESS">SUCCESS</SelectItem>
                        <SelectItem value="FAILED">FAILED</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* View Button */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground opacity-0">View</label>
                    <Button
                      onClick={handleView}
                      disabled={isLoading || !fromDate || !toDate}
                      className="w-full"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "View"
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Transactions Table */}
            <Card className="shadow-sm border-border/60">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl text-foreground">
                  Transactions ({transactions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-3 text-muted-foreground">Loading transactions...</span>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">
                      {transactions.length === 0 && !isLoading
                        ? "No transactions found. Click 'View' to fetch transactions."
                        : "No transactions found for the selected criteria."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[250px]">Order ID</TableHead>
                          <TableHead className="w-[150px]">Amount</TableHead>
                          <TableHead className="w-[150px]">Payment Status</TableHead>
                          <TableHead className="w-[200px]">Transaction ID</TableHead>
                          <TableHead className="w-[150px]">Payment Type</TableHead>
                          <TableHead className="w-[200px]">Created Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((transaction, index) => (
                          <TableRow key={`${transaction.orderId}-${index}`}>
                            <TableCell className="font-medium">{transaction.orderId}</TableCell>
                            <TableCell className="font-semibold text-primary">
                              {formatCurrency(transaction.amount)}
                            </TableCell>
                            <TableCell>{getStatusBadge(transaction.paymentStatus)}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {transaction.transactionId || "-"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {transaction.paymentGroup || "-"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(transaction.createdDate)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default TransactionsList;
